import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

/**
 * Fetch all photos for a specific vehicle
 * @param {number} vehicleId - The vehicle ID
 * @returns {Promise<Array>} Array of photo objects
 */
export const fetchVehiclePhotos = async (vehicleId) => {
    try {
        const response = await axios.get(`${apiUrl}/api/photos/vehicle/${vehicleId}`);
        if (response.data.success) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        if (error.response?.status === 404) {
            // No photos exist yet, return empty array
            return [];
        }
        console.error("Error fetching existing photos:", error);
        throw new Error("Failed to fetch existing photos");
    }
};

/**
 * Upload a single photo with metadata
 * @param {Object} params - Upload parameters
 * @param {File} params.file - The image file
 * @param {number} params.vehicleId - The vehicle ID
 * @param {string} params.photoName - Name of the photo
 * @param {string} params.category - Photo category
 * @param {string} params.description - Photo description
 * @param {Array} params.tags - Array of tags
 * @param {number} params.templateId - Template ID for the photo
 * @returns {Promise<Object>} Upload response data
 */
export const uploadSinglePhoto = async ({ file, vehicleId, photoName, subTag, category, description, tags, templateId }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('vehicleId', vehicleId);
    formData.append('photoName', photoName);
    if (subTag != null) {
        formData.append('subTag', subTag);
    }
    formData.append('category', category);
    formData.append('description', description);
    formData.append('tags', JSON.stringify(tags || []));
    if (templateId) {
        formData.append('templateId', templateId);
    }

    const response = await axios.post(`${apiUrl}/api/photos/upload`, formData);

    return response.data;
};

/**
 * Upload multiple photos with metadata
 * @param {Object} params - Upload parameters
 * @param {number} params.vehicleId - The vehicle ID
 * @param {Array} params.files - Array of File objects
 * @param {Array} params.photoMetadata - Array of metadata objects
 * @returns {Promise<Object>} Upload response data
 */
export const uploadMultiplePhotos = async ({ vehicleId, files, photoMetadata }) => {
    const formData = new FormData();
    formData.append('vehicleId', vehicleId);

    // Append all files
    files.forEach(file => {
        formData.append('photos', file);
    });

    // Append metadata as JSON string
    formData.append('photoMetadata', JSON.stringify(photoMetadata));

    const response = await axios.post(`${apiUrl}/api/photos/upload-photos`, formData);

    return response.data;
};

/**
 * Delete a photo and all its versions
 * @param {string} fileId - The file ID to delete
 * @returns {Promise<Object>} Delete response data
 */
export const deletePhoto = async (fileId) => {
    const response = await axios.delete(`${apiUrl}/api/photos/photo/${fileId}`);
    return response.data;
};

/**
 * Update/replace an existing photo
 * @param {Object} params - Update parameters
 * @param {string} params.fileId - The file ID to update
 * @param {File} params.file - The new image file
 * @param {string} params.photoName - Photo name
 * @param {string} params.category - Photo category
 * @param {string} params.description - Photo description
 * @param {Array} params.tags - Array of tags
 * @param {number} params.templateId - Template ID for the photo
 * @returns {Promise<Object>} Update response data
 */
export const updatePhoto = async ({ fileId, file, photoName, category, description, tags, templateId }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photoName', photoName);
    formData.append('category', category || '');
    formData.append('description', description || '');
    formData.append('tags', tags ? JSON.stringify(tags) : '[]');
    if (templateId) {
        formData.append('templateId', templateId);
    }

    const response = await axios.put(`${apiUrl}/api/photos/photo/${fileId}`, formData);

    return response.data;
};

/**
 * Fetch cloud photo count summary for all vehicles by fetching cloud
 * photos per vehicle in batches using the existing per-vehicle endpoint.
 * @param {Array} vehicleList - Array of vehicle objects from admin list
 * @returns {Promise<Object>} Object keyed by vehicle_id with photo_count and last_upload
 */
export const fetchPhotoSummary = async (vehicleList = []) => {
    if (vehicleList.length === 0) return {};

    const summary = {};
    const BATCH_SIZE = 10;

    for (let i = 0; i < vehicleList.length; i += BATCH_SIZE) {
        const batch = vehicleList.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(
            batch.map(v =>
                fetchVehiclePhotos(v.id).then(photos => ({ id: v.id, photos }))
            )
        );
        for (const result of results) {
            if (result.status === 'fulfilled' && result.value.photos.length > 0) {
                const { id, photos } = result.value;
                const lastUpload = photos.reduce((latest, p) => {
                    const d = p.updated_at || p.created_at;
                    return d && d > latest ? d : latest;
                }, '');
                summary[id] = {
                    photo_count: photos.length,
                    last_upload: lastUpload || null
                };
            }
        }
    }

    return summary;
};

/**
 * Get a specific photo by file ID
 * @param {string} fileId - The file ID
 * @returns {Promise<Object>} Photo data
 */
export const getPhotoById = async (fileId) => {
    const response = await axios.get(`${apiUrl}/api/photos/photo/${fileId}`);
    if (response.data.success) {
        return response.data.data;
    }
    throw new Error("Photo not found");
};
